import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import { FRAME_MATERIALS, FRAME_RIM_TYPES, requiresMountingData } from '@lensmira/domain'

import { useTranslation } from '../i18n.js'
import { frameMaterialLabel, frameRimTypeLabel } from '../status-labels.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Putting a frame on file. §6.4 `CAT-001`, `CAT-002`, `CAT-003`, `CAT-031`; Appendix I.
 *
 * **A shop could be seeded with two frames and never add a third.** `insert into frame_model`
 * appeared once outside tests, in `tools/seed`, and the same for `frame_variant`: no route, no
 * screen, no service. Measured on the deployment before this slice: all six tenants held zero
 * `frame_model` rows. `FrameChoice` reads the catalogue, so every walkthrough of a sale ran against
 * seeded rows and nobody noticed there was no way to make more.
 *
 * **The rim type is a chooser and not a box**, which is the fix for a real defect rather than a
 * convenience. `tools/seed` wrote `half_rim` and `packages/domain/src/frames.ts` asked whether the
 * rim was `semi_rimless`, so `CAT-003`'s lab guard never fired on a seeded semi-rimless frame. The
 * vocabulary is Appendix E's now, and a chooser is what stops a third spelling arriving by hand.
 *
 * **Mounting data is asked for while the frame is being entered**, not when the job is sent.
 * `CAT-003` says the data is "associated with the frame", and `assertSendableToLab` refuses the
 * send: without this a shop enters a rimless frame, sells it, raises the job and is told then, with
 * a customer waiting and a deposit taken.
 *
 * Every write goes through the queue (`OFF-001a`). The server refuses an out-of-range measurement
 * as a 422 sentence rather than letting the database answer 23514, because a 500 in the queue is
 * retried forever with every write behind it blocked.
 */

export interface FrameCatalogueProps {
  /** Appendix B's `Catalogue - edit`, owner and manager. */
  readonly canEdit: boolean
  readonly onWritten?: () => void
}

export function FrameCatalogue({ canEdit, onWritten }: FrameCatalogueProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [brand, setBrand] = useState('')
  const [model, setModel] = useState('')
  const [collection, setCollection] = useState('')
  const [modelCode, setModelCode] = useState('')
  const [material, setMaterial] = useState('')
  const [rimType, setRimType] = useState('')
  const [fitSegment, setFitSegment] = useState('')
  const [colourCode, setColourCode] = useState('')
  const [colourName, setColourName] = useState('')
  const [eyeSizeMm, setEyeSizeMm] = useState('')
  const [bridgeMm, setBridgeMm] = useState('')
  const [templeMm, setTempleMm] = useState('')
  const [mountingNotes, setMountingNotes] = useState('')
  const [barcode, setBarcode] = useState('')
  const [udi, setUdi] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  /**
   * The form is cleared when the write settles, not when the button is pressed.
   *
   * `OFF-001a`'s queue can refuse a write minutes later, and a form cleared on submit leaves the
   * shopkeeper with a refusal and nothing to correct: they would have to read the brand, model and
   * three measurements off the frame again.
   */
  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setDone(t('frameCatalogue.saved', { brand, model }))
    setBrand('')
    setModel('')
    setCollection('')
    setModelCode('')
    setFitSegment('')
    setColourCode('')
    setColourName('')
    setEyeSizeMm('')
    setBridgeMm('')
    setTempleMm('')
    setMountingNotes('')
    setBarcode('')
    setUdi('')
    onWritten?.()
  })

  /**
   * `CAT-003` in the screen as well as the server.
   *
   * Asked before the write so a shopkeeper is told while they are holding the frame, rather than
   * by a refusal after they have pressed Save. The server refuses it too, which is what makes this
   * a courtesy rather than the rule.
   */
  const needsMounting = requiresMountingData(rimType === '' ? undefined : rimType)

  const save = useCallback(async () => {
    if (brand.trim() === '' || model.trim() === '') {
      setError(t('frameCatalogue.brandAndModelRequired'))
      return
    }
    if (colourCode.trim() === '') {
      // `CAT-002`: stock, price and barcode attach to the variant, and a variant is identified by
      // its colour. A model with no variant is a row nothing can point at.
      setError(t('frameCatalogue.colourRequired'))
      return
    }
    if (needsMounting && mountingNotes.trim() === '') {
      setError(t('frameCatalogue.mountingRequired'))
      return
    }
    setError(null)
    setDone(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/catalogue/frames',
      label: t('frameCatalogue.queueLabel', { brand: brand.trim(), model: model.trim() }),
      body: {
        brand: brand.trim(),
        model: model.trim(),
        ...(collection.trim() === '' ? {} : { collection: collection.trim() }),
        ...(modelCode.trim() === '' ? {} : { modelCode: modelCode.trim() }),
        ...(material === '' ? {} : { material }),
        ...(rimType === '' ? {} : { rimType }),
        ...(fitSegment.trim() === '' ? {} : { fitSegment: fitSegment.trim() }),
        variant: {
          colourCode: colourCode.trim(),
          ...(colourName.trim() === '' ? {} : { colourName: colourName.trim() }),
          // Sent as typed. The server bounds them against Appendix I and answers with the bound,
          // because a shopkeeper reading 72 off a temple arm needs to know 70 is the top.
          ...(eyeSizeMm.trim() === '' ? {} : { eyeSizeMm: Number(eyeSizeMm) }),
          ...(bridgeMm.trim() === '' ? {} : { bridgeMm: Number(bridgeMm) }),
          ...(templeMm.trim() === '' ? {} : { templeMm: Number(templeMm) }),
          ...(barcode.trim() === '' ? {} : { barcode: barcode.trim() }),
          ...(udi.trim() === '' ? {} : { udi: udi.trim() }),
          ...(mountingNotes.trim() === '' ? {} : { mountingNotes: mountingNotes.trim() }),
        },
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [
    barcode,
    brand,
    bridgeMm,
    collection,
    colourCode,
    colourName,
    enqueue,
    eyeSizeMm,
    fitSegment,
    material,
    model,
    modelCode,
    mountingNotes,
    needsMounting,
    rimType,
    t,
    templeMm,
    udi,
  ])

  if (!canEdit) {
    return (
      <section className="lm-screen" aria-labelledby="frame-catalogue-heading">
        <h1 id="frame-catalogue-heading" className="lm-screen__heading">
          {t('frameCatalogue.heading')}
        </h1>
        {/* Appendix B gives `Catalogue - edit` to the owner and the manager. A form whose Save is a
            403 would halt the write queue with everything behind it (`OFF-001a`). */}
        <p className="lm-hint">{t('frameCatalogue.notYours')}</p>
      </section>
    )
  }

  return (
    <section className="lm-screen" aria-labelledby="frame-catalogue-heading">
      <h1 id="frame-catalogue-heading" className="lm-screen__heading">
        {t('frameCatalogue.heading')}
      </h1>
      <p className="lm-setup-panel__effects">{t('frameCatalogue.means')}</p>

      <section className="lm-setup-panel" aria-labelledby="frame-model-heading">
        <h2 id="frame-model-heading" className="lm-setup-panel__heading">
          {t('frameCatalogue.modelHeading')}
        </h2>

        <label className="lm-field">
          <span className="lm-field__label">{t('frameCatalogue.brand')}</span>
          <input
            className="lm-input"
            dir="auto"
            value={brand}
            autoComplete="off"
            onChange={(e) => setBrand(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('frameCatalogue.model')}</span>
          <input
            className="lm-input"
            dir="auto"
            value={model}
            autoComplete="off"
            onChange={(e) => setModel(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('frameCatalogue.collection')}</span>
          <input
            className="lm-input"
            dir="auto"
            value={collection}
            autoComplete="off"
            onChange={(e) => setCollection(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('frameCatalogue.modelCode')}</span>
          {/* `LOC-005`: a code printed on a frame is Latin whatever the page direction. */}
          <input
            className="lm-input"
            dir="ltr"
            value={modelCode}
            autoComplete="off"
            onChange={(e) => setModelCode(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('frameCatalogue.material')}</span>
          <select
            className="lm-input"
            value={material}
            onChange={(e) => setMaterial(e.currentTarget.value)}
          >
            <option value="">{t('frameCatalogue.notSaid')}</option>
            {FRAME_MATERIALS.map((value) => (
              <option key={value} value={value}>
                {frameMaterialLabel(t, value)}
              </option>
            ))}
          </select>
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('frameCatalogue.rimType')}</span>
          {/* A chooser and not a box: `tools/seed` wrote `half_rim` while the domain asked about
              `semi_rimless`, so `CAT-003`'s guard never fired on a seeded semi-rimless frame. */}
          <select
            className="lm-input"
            value={rimType}
            onChange={(e) => setRimType(e.currentTarget.value)}
          >
            <option value="">{t('frameCatalogue.notSaid')}</option>
            {FRAME_RIM_TYPES.map((value) => (
              <option key={value} value={value}>
                {frameRimTypeLabel(t, value)}
              </option>
            ))}
          </select>
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('frameCatalogue.fitSegment')}</span>
          {/* Free text, because `CAT-001` names the field and lists no values: `OPEN.md` §4.1. */}
          <input
            className="lm-input"
            dir="auto"
            value={fitSegment}
            autoComplete="off"
            onChange={(e) => setFitSegment(e.currentTarget.value)}
          />
        </label>
      </section>

      <section className="lm-setup-panel" aria-labelledby="frame-variant-heading">
        <h2 id="frame-variant-heading" className="lm-setup-panel__heading">
          {t('frameCatalogue.variantHeading')}
        </h2>
        {/* `CAT-002`: stock, price and barcode attach to the variant, not the model, so a frame is
            not on file until it has one. */}
        <p className="lm-setup-panel__effects">{t('frameCatalogue.variantMeans')}</p>

        <label className="lm-field">
          <span className="lm-field__label">{t('frameCatalogue.colourCode')}</span>
          <input
            className="lm-input"
            dir="ltr"
            value={colourCode}
            autoComplete="off"
            onChange={(e) => setColourCode(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('frameCatalogue.colourName')}</span>
          {/* `2000` is not a colour anybody asks for, which is why `CAT-001` names both. */}
          <input
            className="lm-input"
            dir="auto"
            value={colourName}
            autoComplete="off"
            onChange={(e) => setColourName(e.currentTarget.value)}
          />
        </label>

        {(
          [
            ['frameCatalogue.eyeSize', eyeSizeMm, setEyeSizeMm],
            ['frameCatalogue.bridge', bridgeMm, setBridgeMm],
            ['frameCatalogue.temple', templeMm, setTempleMm],
          ] as const
        ).map(([key, value, set]) => (
          <label key={key} className="lm-field">
            <span className="lm-field__label">{t(key)}</span>
            {/* `inputMode="numeric"`: a millimetre is a whole number and a shop tablet should offer
                the number pad. `dir="ltr"` because a figure never mirrors (`LOC-005`). */}
            <input
              className="lm-input"
              dir="ltr"
              inputMode="numeric"
              value={value}
              autoComplete="off"
              onChange={(e) => set(e.currentTarget.value)}
            />
          </label>
        ))}

        <label className="lm-field">
          <span className="lm-field__label">{t('frameCatalogue.barcode')}</span>
          <input
            className="lm-input"
            dir="ltr"
            value={barcode}
            autoComplete="off"
            onChange={(e) => setBarcode(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('frameCatalogue.udi')}</span>
          {/* `CAT-031`'s device identifier. Unvalidated: a UDI-DI is issued by GS1, HIBCC or
              ICCBBA and each has its own grammar, so checking the wrong one refuses a real code. */}
          <input
            className="lm-input"
            dir="ltr"
            value={udi}
            autoComplete="off"
            onChange={(e) => setUdi(e.currentTarget.value)}
          />
        </label>

        {needsMounting ? (
          <label className="lm-field">
            <span className="lm-field__label">{t('frameCatalogue.mounting')}</span>
            {/* Shown only for the rim types that need it, and required when shown. `CAT-003` says
                the data is "associated with the frame", which is now rather than at send. */}
            <textarea
              className="lm-input"
              dir="auto"
              rows={3}
              value={mountingNotes}
              onChange={(e) => setMountingNotes(e.currentTarget.value)}
            />
          </label>
        ) : null}
        {needsMounting ? (
          <p className="lm-setup-panel__effects">{t('frameCatalogue.mountingMeans')}</p>
        ) : null}
      </section>

      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={writeId !== null}
        onClick={() => {
          void save()
        }}
      >
        {t('frameCatalogue.save')}
      </button>

      {error === null ? null : (
        <p className="lm-error" role="alert">
          {error}
        </p>
      )}
      {done === null ? null : (
        <p className="lm-hint" role="status">
          {done}
        </p>
      )}
      {writeId === null ? null : (
        <p className="lm-hint" role="status">
          {statusLine(t, snapshot, writeId, t('frameCatalogue.queueSubject'))}
        </p>
      )}
    </section>
  )
}
